import {
  AlertTriangle,
  ArrowUpRight,
  Loader2,
  Pause,
  Play,
  Square,
  Workflow,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ChatArtifactCards } from '@/components/chat-artifact-cards';
import { useNotification } from '@/components/notifications';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { workflowRunPath } from '@/constants/workflow-editor';
import { useWorkflowSummary } from '@/hooks/queries/workflow/use-workflow-summary';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { signalAgentRunLifecycle } from '@/services/agents/run-lifecycle';
import { formatRunCost } from '@/utils/format-cost';

import { useAgentChatContext } from './agent-chat-provider';
import { savedWorkflowId } from './paused-node';
import { isTerminalRunStatus } from './stored-run-state';

import { type AgentChatRunStatus, useAgentChatRun } from './use-agent-chat-run';
import { useRunArtifactCards } from './use-run-artifact-cards';

/**
 * What the card displays, which is not quite what the run reports: `stopping`
 * is the window between the backend accepting a stop and the run actually
 * ending, which the run's own status has no spelling for.
 */
type CardStatus = AgentChatRunStatus | 'stopping';

function formatElapsedClock(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/**
 * Stand-in for a historical run's card while it hydrates. Without this, the
 * card just appears once the backfill resolves, a few seconds into
 * mounting -- reading to the user as a run that was newly added, rather than
 * one that was already there and is still loading.
 */
function WorkflowRunCardSkeleton() {
  return (
    <div
      data-testid='workflow-run-card-skeleton'
      className='w-full overflow-hidden rounded-xl bg-accent p-4 shadow-sm'
    >
      <div className='flex items-start gap-3'>
        <div className='flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-background shadow-sm'>
          <Workflow className='size-6 text-muted-foreground/40' />
        </div>
        <div className='min-w-0 flex-1 space-y-2'>
          {/* Skeleton defaults to bg-accent, same as this card's own
              background -- bg-background/60 read fine in dark mode (accent
              and background sit far apart there) but nearly vanished in
              light mode, where accent is already almost white. muted-foreground
              is defined with a real lightness gap from accent in both
              themes, so the pulse stays visible either way. */}
          <Skeleton className='h-4 w-40 bg-muted-foreground/25' />
          <Skeleton className='h-3 w-56 bg-muted-foreground/25' />
          <Skeleton className='mt-2 h-2 w-full bg-muted-foreground/25' />
        </div>
      </div>
    </div>
  );
}

export function WorkflowRunCard({
  runId,
  dispatchWorkflowId,
  isHistorical = false,
}: {
  runId: string;
  /**
   * Workflow that was open on the canvas when this run was dispatched from
   * chat, for a canvas-dispatched run -- whose run_started frame never
   * carries a workflow id, see `effectiveWorkflowId` below. Undefined for a
   * run the agent started itself, which does carry one.
   */
  dispatchWorkflowId?: string | null;
  /** See the same field on useAgentChatRun. */
  isHistorical?: boolean;
}) {
  const { orgId, agentId, onOpenWorkflow, visibleWorkflowId } =
    useAgentChatContext();
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const run = useAgentChatRun({
    orgId,
    agentId,
    runId,
    workflowId: dispatchWorkflowId,
    isHistorical,
  });
  const [now, setNow] = useState(Date.now());
  const [action, setAction] = useState<'pause' | 'resume' | 'stop' | null>(
    null,
  );
  const [localStatus, setLocalStatus] = useState<AgentChatRunStatus | null>(
    null,
  );
  // A stop the user asked for and the backend accepted, while the run is
  // still winding down.
  const [stopRequested, setStopRequested] = useState(false);

  /** How the run ended, as the backend reports it, or null while it runs. */
  const outcome = isTerminalRunStatus(run.status) ? run.status : null;

  // The event stream has no vocabulary for a run that was stopped: the
  // gateway ends a torn-down run with `workflow.run_completed` and closes the
  // stream in TASK_STATE_COMPLETED -- the very frames a natural finish sends.
  // So the stop the user asked for is the only record that it happened, and it
  // outranks that frame: otherwise the card announces "Run complete" for a run
  // the user just killed, and then contradicts itself on reload, where the
  // persisted `stopped` row does read as stopped.
  // A stop only ever disguises itself as a *completed* outcome (see above) --
  // a genuine `workflow.run_failed` racing an in-flight stop request is a
  // real failure, and must keep showing as one, error message included,
  // rather than being swallowed into "Run stopped".
  const status: CardStatus =
    stopRequested && outcome === 'completed'
      ? 'canceled'
      : (outcome ?? (stopRequested ? 'stopping' : (localStatus ?? run.status)));

  useEffect(() => {
    if (run.status === 'paused' || run.status === 'running') {
      setLocalStatus(null);
    }
  }, [run.status]);

  // Whether the clock should be frozen right now: a confirmed backend pause
  // (`run.pausedSince`), or the optimistic instant right after the user
  // clicks Pause, before that confirmation lands (`status === 'paused'` from
  // `localStatus`, set in handleLifecycle below) -- otherwise the "Run
  // paused" label shows while the clock keeps ticking for however long the
  // stream event takes to arrive. A stop fired from a paused run falls
  // under the first case too: the backend hasn't resumed it, so
  // `pausedSince` stays set and the clock stays parked through "Stopping
  // run…" the same way.
  const parked = status === 'paused' || !!run.pausedSince;

  const wasParkedRef = useRef(parked);
  useEffect(() => {
    if (parked) {
      wasParkedRef.current = true;
      return;
    }
    if (!wasParkedRef.current) return;
    wasParkedRef.current = false;
    // The clock stopped ticking when the pause began, so `now` is still the
    // last tick before it. Catching it up the instant the pause ends keeps
    // it from disagreeing with the parked total: a render that subtracted
    // the parked span from a stale `now` showed the clock jumping backwards
    // until the next tick caught up.
    setNow(Date.now());
  }, [parked]);

  useEffect(() => {
    // A parked run is not spending time, so the clock does not advance.
    if (
      !run.startedAt ||
      run.completedAt ||
      parked ||
      isTerminalRunStatus(status)
    ) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [run.startedAt, run.completedAt, parked, status]);

  // A canvas-dispatched run's run_started frame never carries a workflow
  // id (the canvas dispatches through the workflow API, not the agent) --
  // fall back to what the canvas had open when the run was kicked off.
  const effectiveWorkflowId = run.workflowId ?? dispatchWorkflowId ?? undefined;
  // 'new' (or an unsaved canvas's draft key, 'new:<createdAt>') names a
  // workflow that was never saved, not one that can be navigated back to or
  // looked up by id -- opening it would land on a blank canvas, not this
  // run's graph, and fetching a summary for it would just 404.
  const viewableWorkflowId = savedWorkflowId(effectiveWorkflowId);

  // The cost is only shown on a finished run, and asking for it costs the
  // backend a Langfuse round trip, so only request it once we are there.
  const { summary, latestRunCostUsd } = useWorkflowSummary(viewableWorkflowId, {
    includeCost: status === 'completed',
  });
  // What the run produced. The launching turn is long over by the time a run
  // finishes, so its deliverables are read back from the artifact table rather
  // than arriving as chat artifact frames.
  // A run the user stopped still hands back whatever had already finished by
  // the time it was killed, so a stop asks for deliverables the same as a
  // natural finish does.
  const deliverables = useRunArtifactCards(runId, {
    enabled: outcome === 'completed' || outcome === 'canceled',
    isHistorical,
  });
  const workflowName = summary?.name ?? 'Workflow run';
  // A pause anchors the clock at the instant it began, so it reads the same on
  // every render until the run moves again.
  const elapsedUntil = run.completedAt ?? run.pausedSince ?? now;
  // A pause that never resumed before the run ended -- a stop fired while
  // paused, most often -- leaves `pausedSince` set alongside `completedAt`.
  // `pausedTotalMs` only grows on a resume, so that final open span needs
  // folding in here, or the run's last parked stretch reads as active work
  // in "Ran for".
  const openPauseMs =
    run.pausedSince && run.completedAt
      ? Math.max(0, run.completedAt - run.pausedSince)
      : 0;
  const elapsed = run.startedAt
    ? Math.max(
        0,
        elapsedUntil - run.startedAt - (run.pausedTotalMs ?? 0) - openPauseMs,
      )
    : 0;
  const terminal = isTerminalRunStatus(status);

  const meta = useMemo(() => {
    if (status === 'completed') {
      const cost = formatRunCost(latestRunCostUsd);
      return [
        'Run complete',
        `Ran for ${formatDuration(elapsed)}`,
        ...(cost ? [`Cost ${cost}`] : []),
      ];
    }
    if (status === 'failed') {
      return ['Run failed', `Ran for ${formatDuration(elapsed)}`];
    }
    if (status === 'canceled') {
      return ['Run stopped', `Ran for ${formatDuration(elapsed)}`];
    }
    return [];
  }, [elapsed, latestRunCostUsd, status]);

  const handleViewWorkflow = () => {
    if (!viewableWorkflowId) return;
    if (onOpenWorkflow) {
      onOpenWorkflow(viewableWorkflowId, runId);
      return;
    }
    navigate(workflowRunPath(viewableWorkflowId, runId));
  };

  const handleLifecycle = async (next: 'pause' | 'resume' | 'stop') => {
    if (!orgId || action) return;
    setAction(next);
    try {
      await signalAgentRunLifecycle(orgId, agentId, runId, next);
      if (next === 'stop') {
        // Not 'canceled' yet -- the run is still tearing down, and claiming a
        // finished run here is what made the card jump from a live run to
        // "Run stopped" to "Run complete" in the space of a few frames.
        setStopRequested(true);
      } else {
        setLocalStatus(next === 'pause' ? 'paused' : 'running');
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'workflow-run-card-lifecycle',
          orgId,
          agentId,
          runId,
          action: next,
        },
      });
      notification.error(
        error instanceof Error ? error.message : `Could not ${next} this run`,
      );
    } finally {
      setAction(null);
    }
  };

  // The canvas beside the chat already reports this run — node statuses, its
  // own timer, its own run controls — so the panel would say it all twice.
  // Before run_started lands, effectiveWorkflowId falls back to what was on
  // screen when the run was dispatched -- not whatever the canvas shows now,
  // which the user may have already switched away from. Computed ahead of
  // the hydrating check below: a canvas-dispatched run knows this from
  // dispatchWorkflowId alone, with no need to wait on the replay.
  const canvasIsShowingThisRun =
    !!visibleWorkflowId && effectiveWorkflowId === visibleWorkflowId;

  // Historical events are replayed when a persisted conversation mounts.
  // Wait for that backfill to collapse into one snapshot rather than showing
  // the old run advance from 0% again -- but still paint a placeholder
  // rather than nothing, or the eventual card reads as newly added rather
  // than one that was already there and is still loading. Suppressed the
  // same as the settled card would be when the canvas already shows this
  // run: otherwise the skeleton would just flash and vanish once hydration
  // resolves.
  if (run.isHydrating) {
    return canvasIsShowingThisRun ? null : <WorkflowRunCardSkeleton />;
  }

  // Deliverables still belong in the chat: they are the run's output, not a
  // repeat of its progress, and the canvas does not surface them.
  if (canvasIsShowingThisRun) {
    return <ChatArtifactCards cards={deliverables} />;
  }

  // (A run whose workflow is already on screen does not reach here — the
  // panel above is suppressed wholesale.)
  const canViewWorkflow = !!viewableWorkflowId;

  return (
    <div className='flex w-full flex-col gap-3'>
      <div
        data-testid='workflow-run-card'
        data-run-status={status}
        className='w-full overflow-hidden rounded-xl bg-accent p-4 shadow-sm'
      >
        <div className='flex items-start gap-3'>
          <div className='flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-background shadow-sm'>
            <Workflow className='size-6' />
          </div>
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-start gap-2'>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-base font-medium text-foreground'>
                  {workflowName}
                </p>
                {terminal ? (
                  <div className='mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
                    {meta.map((item, index) => (
                      <span key={item} className='contents'>
                        {index > 0 && (
                          <span className='size-0.5 rounded-full bg-muted-foreground' />
                        )}
                        <span>{item}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className='mt-1 truncate text-sm text-muted-foreground'>
                    {status === 'paused'
                      ? 'Run paused'
                      : status === 'stopping'
                        ? 'Stopping run…'
                        : run.currentEvent}
                  </p>
                )}
              </div>
              {canViewWorkflow && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-8 shrink-0 gap-2 text-xs'
                  onClick={handleViewWorkflow}
                >
                  View Workflow
                  <ArrowUpRight className='size-4' />
                </Button>
              )}
            </div>

            {!terminal && (
              <div className='mt-3 flex items-center gap-4'>
                <Progress
                  value={run.progress}
                  shimmer={status === 'running'}
                  className='min-w-20 flex-1'
                />
                <span className='w-10 text-right text-sm text-muted-foreground'>
                  {run.progress}%
                </span>
                <span className='font-mono text-sm text-muted-foreground'>
                  {formatElapsedClock(elapsed)}
                </span>
                <div className='flex shrink-0 items-center gap-1'>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='size-9'
                    aria-label={
                      status === 'paused' ? 'Resume run' : 'Pause run'
                    }
                    disabled={!!action || status === 'stopping'}
                    onClick={() =>
                      handleLifecycle(status === 'paused' ? 'resume' : 'pause')
                    }
                  >
                    {action === 'pause' || action === 'resume' ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : status === 'paused' ? (
                      <Play className='size-4' />
                    ) : (
                      <Pause className='size-4' />
                    )}
                  </Button>
                  <Button
                    variant='destructive'
                    size='icon'
                    className='size-9'
                    aria-label='Stop run'
                    disabled={!!action || status === 'stopping'}
                    onClick={() => handleLifecycle('stop')}
                  >
                    {action === 'stop' || status === 'stopping' ? (
                      <Loader2 className='size-4 animate-spin' />
                    ) : (
                      <Square className='size-4 fill-current' />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {status === 'failed' && run.error && (
              <div className='mt-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive'>
                <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
                <span>{run.error}</span>
              </div>
            )}
            {run.connectionError && !terminal && (
              <p className='mt-2 text-xs text-muted-foreground'>
                {run.connectionError}
              </p>
            )}
          </div>
        </div>
      </div>
      <ChatArtifactCards cards={deliverables} />
    </div>
  );
}
